/* Reusable settings panels — used by both buyer and seller settings pages.
   Each panel is self-contained (fetches its own data, saves via its own endpoint). */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FieldRow, SectionHeader, ComingSoon } from "./SettingsShell";
import { toast } from "sonner";
import DocUploadField from "./DocUploadField";
import RevealableNumber from "./RevealableNumber";
import {
  Loader2, Plus, Trash2, Mail, Phone, Briefcase, User, Globe,
  FileText, Copy, Building2, Check, Sparkles, AlertCircle, X,
  CreditCard, KeyRound, ShieldCheck, Users, Clock, Send,
  Laptop, MapPin, AlertTriangle, Filter, Search, CheckCircle2,
} from "lucide-react";

// ── Profile Info ────────────────────────────────────────────────────────
export function ProfileInfoPanel() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", job_title: "" });
  const [saving, setSaving] = useState(false);

  // Phone-change is locked to OWNER-only and requires SMS confirmation
  // from the CURRENT phone (Jun 2026 hardening). Non-owners see the
  // phone as a read-only display.
  const isOwner = (user?.team_flags?.can_settings !== false);
  const [phoneChangeStep, setPhoneChangeStep] = useState("idle"); // idle | init | otp | done
  const [phoneChangeCtx,  setPhoneChangeCtx]  = useState(null);
  const [phoneChangeCode, setPhoneChangeCode] = useState("");
  const [phoneChangeErr,  setPhoneChangeErr]  = useState(null);
  const [newPhoneInput,   setNewPhoneInput]   = useState("");
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      job_title: user.job_title || "",
    });
    setNewPhoneInput(user?.phone || "");
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      // Phone is no longer sent through this endpoint — backend rejects
      // it. Profile saves now only carry name + job_title.
      await api.patch("/profile", { name: form.name, job_title: form.job_title });
      await refresh?.();
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const initPhoneChange = async () => {
    setPhoneChangeErr(null);
    if (!newPhoneInput || newPhoneInput === user?.phone) {
      setPhoneChangeErr("Enter a new phone number that's different from your current one.");
      return;
    }
    setPhoneSubmitting(true);
    try {
      const { data } = await api.post("/profile/phone/init-change", { new_phone: newPhoneInput.trim() });
      setPhoneChangeCtx(data);
      setPhoneChangeStep("otp");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "Could not start phone change.";
      setPhoneChangeErr(typeof msg === "string" ? msg : "Phone-change failed.");
    } finally { setPhoneSubmitting(false); }
  };

  const confirmPhoneChange = async () => {
    if (!phoneChangeCtx?.challenge_id || phoneChangeCode.length < 4) return;
    setPhoneChangeErr(null);
    setPhoneSubmitting(true);
    try {
      const { data } = await api.post("/profile/phone/confirm-change", {
        challenge_id: phoneChangeCtx.challenge_id, code: phoneChangeCode.trim(),
      });
      await refresh?.();
      setPhoneChangeStep("done");
      toast.success("Phone number updated — re-verify it in Security to enable SMS step-up.");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "That code didn't work.";
      setPhoneChangeErr(typeof msg === "string" ? msg : "Verification failed.");
    } finally { setPhoneSubmitting(false); }
  };

  return (
    <div>
      <SectionHeader title="Profile info" desc="Your personal details. Only you can edit your own profile — these don't affect your team."/>
      <div>
        <FieldRow label="Full name" hint="Shown on invoices and audit logs">
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="profile-name"/>
        </FieldRow>
        <FieldRow label="Work email" hint="Primary login identifier">
          <Input value={user?.email || ""} disabled className="bg-secondary/40" data-testid="profile-email"/>
          <p className="text-[11px] text-foreground/50 mt-1">Email changes require verification — contact support.</p>
        </FieldRow>

        {/* Phone — Owner-only, SMS-confirmed change flow (Jun 2026) */}
        <FieldRow label="Phone number" hint={isOwner ? "Changes require SMS confirmation" : "Locked — only the account owner can change phone numbers"}>
          {!isOwner ? (
            <>
              <Input value={user?.phone || ""} disabled className="bg-secondary/40 font-mono" data-testid="profile-phone-locked"/>
              <p className="text-[11px] text-foreground/50 mt-1">
                Team members can't change their phone here. Ask the account owner to update it for you, or contact support.
              </p>
            </>
          ) : phoneChangeStep === "idle" ? (
            <>
              <div className="flex items-center gap-2">
                <Input value={user?.phone || "(none)"} disabled className="bg-secondary/40 font-mono flex-1" data-testid="profile-phone-current"/>
                <Button size="sm" variant="outline" onClick={() => setPhoneChangeStep("init")}
                        data-testid="profile-phone-change-btn"
                        className="rounded-full shrink-0">
                  Change phone
                </Button>
              </div>
              <p className="text-[11px] text-foreground/50 mt-1 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3"/>Phone changes are gated by SMS code from your current number.
              </p>
            </>
          ) : phoneChangeStep === "init" ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50/40 p-3 space-y-3">
              <div className="text-[12px] text-amber-900">
                <strong>Step 1 of 2:</strong> Enter the new phone number. We'll text a confirmation code to your <strong>current</strong> phone ({user?.phone}) — only then will the change apply.
              </div>
              <Input
                type="tel" value={newPhoneInput} onChange={e => setNewPhoneInput(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="profile-phone-new-input"
                className="bg-white"
              />
              {phoneChangeErr && (
                <div className="text-[12px] text-rose-700">{phoneChangeErr}</div>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={initPhoneChange} disabled={phoneSubmitting}
                        className="rounded-full bg-foreground text-background"
                        data-testid="profile-phone-init-submit">
                  {phoneSubmitting ? "Sending code…" : "Send code to current phone"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setPhoneChangeStep("idle"); setPhoneChangeErr(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : phoneChangeStep === "otp" ? (
            <div className="rounded-lg border border-sky-300 bg-sky-50/40 p-3 space-y-3">
              <div className="text-[12px] text-sky-900">
                <strong>Step 2 of 2:</strong> Enter the 6-digit code we just texted to <strong>{phoneChangeCtx?.masked_current_phone}</strong>. After this, your number changes to <strong>{phoneChangeCtx?.masked_new_phone}</strong> and you'll re-verify the new number under Security.
                {phoneChangeCtx?.dev_mode && <span className="block mt-1 italic">DEV mode · any digit code works.</span>}
              </div>
              <Input
                value={phoneChangeCode}
                onChange={e => setPhoneChangeCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                placeholder="123456"
                inputMode="numeric" autoComplete="one-time-code"
                data-testid="profile-phone-otp-input"
                className="bg-white text-center text-xl tracking-[0.3em] font-mono"
              />
              {phoneChangeErr && (
                <div className="text-[12px] text-rose-700">{phoneChangeErr}</div>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={confirmPhoneChange} disabled={phoneSubmitting || phoneChangeCode.length < 4}
                        className="rounded-full bg-foreground text-background"
                        data-testid="profile-phone-confirm-submit">
                  {phoneSubmitting ? "Verifying…" : "Verify & change"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setPhoneChangeStep("idle"); setPhoneChangeCode(""); setPhoneChangeErr(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50/40 p-3 text-[12.5px] text-emerald-900 flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>
                Phone updated to <strong>{user?.phone}</strong>. Go to Settings → Security to verify the new number so SMS step-up will work.
              </div>
            </div>
          )}
        </FieldRow>

        <FieldRow label="Job title" hint="How you sign internal documents">
          <Input value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} placeholder="Head of Wholesale" data-testid="profile-title"/>
        </FieldRow>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving} data-testid="profile-save" className="rounded-full bg-foreground text-background">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

// ── Business Details ────────────────────────────────────────────────────
// COI sub-row: pairs the upload field with an optional expiry date so the
// SC-5.3 factoring eligibility gate can flag expired certificates.
function CoiUploadRow() {
  const [expiry, setExpiry] = useState("");
  return (
    <FieldRow
      label="Certificate of Insurance (COI)"
      hint="General + product liability policy — required to unlock invoice factoring"
    >
      <div id="kyb-coi-row" className="space-y-2">
        <div>
          <Label className="text-[11.5px] text-foreground/55">Policy expiry date <span className="text-foreground/40">(optional but recommended)</span></Label>
          <Input
            type="date"
            value={expiry}
            onChange={e => setExpiry(e.target.value)}
            data-testid="kyb-coi-expiry"
            className="mt-1 h-9 max-w-[200px]"
          />
        </div>
        <DocUploadField
          docType="coi"
          accept="application/pdf"
          acceptLbl="PDF"
          testIdPrefix="kyb-coi"
          extraFormFields={expiry ? { expires_at: expiry } : null}
        />
      </div>
    </FieldRow>
  );
}

// Maps backend `business_type` codes (set during registration) → the
// display labels used by the <select>. Falls through unchanged if the
// value is already in label form (e.g. "LLC").
const BUSINESS_TYPE_LABELS = {
  llc:         "LLC",
  corp:        "C-Corp",
  c_corp:      "C-Corp",
  s_corp:      "S-Corp",
  sole_prop:   "Sole Prop",
  partnership: "Partnership",
  nonprofit:   "Non-Profit",
  other:       "LLC",
};
function normaliseBusinessType(v) {
  if (!v) return "LLC";
  const key = String(v).toLowerCase();
  return BUSINESS_TYPE_LABELS[key] || v;
}

export function BusinessDetailsPanel() {
  const { user, refresh } = useAuth();
  const [kybDocs, setKybDocs] = useState([]);
  const [form, setForm] = useState({
    legal_name: "", business_type: "LLC", ein: "", website: "",
    address_line1: "", city: "", state: "", zip: "", country: "US",
  });
  const [saving, setSaving] = useState(false);
  // ── Step-up state (Jun 2026 hardening) ──
  // Business detail saves now require TOTP-or-SMS verification when ANY
  // business field actually changes. Same 2-stage pattern as password
  // change: first POST returns 400 stepup_required → we render the
  // factor picker → user enters code → re-submit with `factor` + code.
  const [stepupStage, setStepupStage] = useState("idle");   // idle | choose | enter
  const [stepupFactor, setStepupFactor] = useState(null);
  const [stepupCode, setStepupCode] = useState("");
  const [stepupAvailable, setStepupAvailable] = useState([]);
  const [stepupPhoneMasked, setStepupPhoneMasked] = useState(null);
  const [stepupSetupPath, setStepupSetupPath] = useState(null);

  const resetStepup = () => {
    setStepupStage("idle"); setStepupFactor(null); setStepupCode("");
    setStepupAvailable([]); setStepupPhoneMasked(null); setStepupSetupPath(null);
  };

  const buildPayload = (extra = {}) => ({
    legal_name:    form.legal_name,
    business_type: form.business_type,
    ein:           form.ein,
    address_line1: form.address_line1,
    city:          form.city,
    state:         form.state,
    zip:           form.zip,
    country:       form.country,
    ...extra,
  });

  // SC-5.3 deep-link: when navigated with ?focus=w9|coi, scroll the target
  // row into view + flash a highlight so the user lands exactly on the
  // missing document. Triggered when SellerCreditPanel's "Fix in Settings"
  // CTA opens this tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (!focus || !["w9", "coi"].includes(focus)) return;
    const ttl = setTimeout(() => {
      const el = document.getElementById(`kyb-${focus}-row`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-lime-400", "rounded-lg",
                       "transition-shadow", "shadow-lg");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-lime-400", "shadow-lg");
      }, 2400);
    }, 350);  // wait for panel mount + tab switch
    return () => clearTimeout(ttl);
  }, []);

  // Hydrate the form from the registered-user record so the buyer/seller
  // never has to re-enter what they typed at signup.
  useEffect(() => {
    if (!user) return;
    const addr = user.address || {};
    setForm(prev => ({
      ...prev,
      legal_name:    user.company || "",
      business_type: normaliseBusinessType(user.business_type),
      ein:           user.ein || "",
      website:       user.website || user.brand?.website || "",
      address_line1: addr.line1 || "",
      city:          addr.city  || "",
      state:         addr.state || "",
      zip:           addr.postal_code || "",
      country:       addr.country || user.country || "US",
    }));
  }, [user]);

  // Doc-status chip still reflects KYB-doc submission state.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/kyb");
        setKybDocs(Array.isArray(data) ? data : []);
      } catch { /* no kyb docs yet */ }
    })();
  }, []);
  const docVerified = kybDocs.some(d => d.status === "verified");

  const save = async () => {
    setSaving(true);
    try {
      // Note: `website` is intentionally NOT in this payload — it lives in
      // Public profile (single source of truth). Edit it there.
      await api.patch("/profile", buildPayload());
      await refresh?.();
      toast.success("Business details saved");
      resetStepup();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "stepup_required") {
        setStepupAvailable(detail.available_factors || []);
        setStepupPhoneMasked(detail.phone_masked || null);
        const preferred = (detail.available_factors || [])[0] || null;
        setStepupFactor(preferred);
        setStepupStage("choose");
        if (preferred === "sms" && (detail.available_factors || []).length === 1) {
          await sendSmsForChange(true);
        }
      } else if (detail?.code === "mfa_required_first") {
        toast.error(detail.message);
        setStepupSetupPath(detail.setup_path || null);
      } else {
        toast.error(detail?.message || detail || "Save failed");
      }
    } finally { setSaving(false); }
  };

  const sendSmsForChange = async (silent = false) => {
    try {
      const { data } = await api.post("/sms/stepup/send", { action: "change_business_details" });
      if (!silent) toast.success(`Code sent to ${data?.masked || "your phone"}`);
      setStepupStage("enter");
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Could not send SMS code");
    }
  };

  const confirmSave = async (e) => {
    e?.preventDefault?.();
    if (!stepupFactor || !stepupCode) return;
    setSaving(true);
    try {
      await api.patch("/profile", buildPayload({
        factor:      stepupFactor,
        verify_code: stepupCode.trim(),
      }));
      await refresh?.();
      toast.success("Business details saved");
      resetStepup();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || "Verification failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <SectionHeader title="Business details" desc="Legal entity info used for KYB verification, invoicing, and compliance. Pre-filled from the info you entered at sign-up. Buyer-facing details (tagline, website, lead time, MOQ) live in Public profile." action={
        docVerified ? <span className="chip bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"><Check className="w-3 h-3"/>Verified</span> : <span className="chip bg-amber-500/15 text-amber-700 border border-amber-500/30">Pending verification</span>
      }/>
      <div>
        <FieldRow label="Legal business name" hint="As it appears on your EIN letter">
          <Input value={form.legal_name} onChange={e => setForm({...form, legal_name: e.target.value})} data-testid="kyb-legal-name"/>
        </FieldRow>
        <FieldRow label="Business type">
          <select value={form.business_type} onChange={e => setForm({...form, business_type: e.target.value})} className="w-full h-10 border border-foreground/15 rounded-md px-2 bg-white text-[14px]" data-testid="kyb-business-type">
            <option value="LLC">LLC</option>
            <option value="C-Corp">C-Corp</option>
            <option value="S-Corp">S-Corp</option>
            <option value="Sole Prop">Sole Proprietorship</option>
            <option value="Partnership">Partnership</option>
            <option value="Non-Profit">Non-Profit</option>
          </select>
        </FieldRow>
        <FieldRow label="EIN / Tax ID" hint={user?.ein_verified ? "Verified with the IRS — locked for compliance" : "9-digit federal tax ID"}>
          {user?.ein_verified ? (
            <div className="flex items-center gap-2" data-testid="kyb-ein-locked">
              <Input value={form.ein} disabled className="bg-secondary/40 font-mono"/>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 text-[10.5px] font-semibold uppercase tracking-wider whitespace-nowrap">
                <Check className="w-3 h-3"/> Verified
              </span>
            </div>
          ) : (
            <Input value={form.ein} onChange={e => setForm({...form, ein: e.target.value})} placeholder="12-3456789" data-testid="kyb-ein"/>
          )}
          {user?.ein_verified && (
            <p className="text-[11px] text-foreground/50 mt-1">
              EIN matched IRS records on {user?.ein_verified_at ? new Date(user.ein_verified_at).toLocaleDateString() : "signup"}. To change it, <a href="mailto:support@verqo.com?subject=EIN%20change%20request" className="underline hover:text-foreground/80">contact support</a>.
            </p>
          )}
        </FieldRow>
        <FieldRow label="Website">
          <div className="text-[12px] text-foreground/55 rounded-lg border border-foreground/10 bg-secondary/30 px-3 py-2 inline-flex items-center gap-2"
               data-testid="kyb-website-ref">
            <Globe className="w-3 h-3"/>
            Your website lives in <a href="#public-profile" onClick={(e) => { e.preventDefault(); window.location.hash = "public-profile"; }} className="underline hover:text-foreground">Public profile</a> — it's a buyer-facing field.
          </div>
        </FieldRow>
        <FieldRow label="Business address" hint="Registered address on your EIN letter">
          <div className="space-y-2">
            <Input placeholder="Street address" value={form.address_line1} onChange={e => setForm({...form, address_line1: e.target.value})} data-testid="kyb-addr"/>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="kyb-city"/>
              <Input placeholder="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} data-testid="kyb-state"/>
              <Input placeholder="ZIP" value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} data-testid="kyb-zip"/>
            </div>
          </div>
        </FieldRow>
        <FieldRow label="EIN letter (CP 575)" hint="IRS-issued EIN assignment letter — PDF only">
          <DocUploadField docType="ein_letter" accept="application/pdf" acceptLbl="PDF" testIdPrefix="kyb-einletter"/>
        </FieldRow>
        <FieldRow label="W-9 form" hint="IRS Form W-9 (signed) — PDF only">
          <div id="kyb-w9-row">
            <DocUploadField docType="w9" accept="application/pdf" acceptLbl="PDF" testIdPrefix="kyb-w9"/>
          </div>
        </FieldRow>
        <CoiUploadRow/>
        <FieldRow label="Articles of incorporation" hint="Speeds up verification — PDF only">
          <DocUploadField docType="articles" accept="application/pdf" acceptLbl="PDF" testIdPrefix="kyb-articles"/>
        </FieldRow>
        <FieldRow label="Bank statements" hint="Add as many statements as you'd like — last 3 months recommended">
          <DocUploadField docType="bank_statement" accept="application/pdf" acceptLbl="PDF" multiple testIdPrefix="kyb-bank"/>
        </FieldRow>
      </div>

      {/* Step-up gate UI (Jun 2026) — only renders when the user just
          tried to save business changes and the backend asked for a
          2-factor code. Idle state shows the normal Save button. */}
      {stepupStage === "idle" ? (
        <div className="flex justify-end items-center gap-3 mt-4">
          {stepupSetupPath && (
            <div className="text-[12px] text-amber-700 inline-flex items-center gap-1.5 mr-auto">
              <AlertCircle className="w-3 h-3"/>
              Set up MFA first → <a className="underline" href={stepupSetupPath}>Settings → Security</a>
            </div>
          )}
          <Button onClick={save} disabled={saving} data-testid="kyb-save" className="rounded-full bg-foreground text-background">
            {saving ? "Saving…" : "Save business details"}
          </Button>
        </div>
      ) : (
        <form onSubmit={confirmSave} className="mt-4 max-w-md space-y-4 rounded-xl bg-secondary/40 border border-border p-4" data-testid="kyb-stepup">
          <div className="flex items-center gap-2 text-[12px] mono uppercase tracking-[0.18em] text-foreground/55">
            <ShieldCheck className="w-3.5 h-3.5"/>
            Confirm with 2-factor
          </div>
          <p className="text-[12.5px] text-foreground/70 leading-relaxed">
            You're about to change KYB-relevant business details (legal name, EIN, address). For account-takeover protection, please confirm with a code from your authenticator app{stepupAvailable.includes("sms") ? " or a Twilio SMS" : ""}.
          </p>

          {stepupAvailable.length > 1 && (
            <div className="flex items-center gap-1.5 bg-background/60 rounded-full p-1 w-fit">
              {stepupAvailable.includes("totp") && (
                <button type="button" onClick={() => { setStepupFactor("totp"); setStepupStage("enter"); }}
                  data-testid="kyb-factor-totp"
                  className={`text-[12px] px-3 py-1 rounded-full ${stepupFactor === "totp" ? "bg-foreground text-background" : "text-foreground/65"}`}>
                  Authenticator
                </button>
              )}
              {stepupAvailable.includes("sms") && (
                <button type="button" onClick={() => { setStepupFactor("sms"); sendSmsForChange(); }}
                  data-testid="kyb-factor-sms"
                  className={`text-[12px] px-3 py-1 rounded-full ${stepupFactor === "sms" ? "bg-foreground text-background" : "text-foreground/65"}`}>
                  SMS code
                </button>
              )}
            </div>
          )}

          {stepupFactor === "totp" && (
            <div className="space-y-1.5">
              <Label className="text-[12px]">6-digit code from your authenticator app</Label>
              <Input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={10} autoFocus
                value={stepupCode} onChange={e => setStepupCode(e.target.value)}
                placeholder="123 456 (or backup code)"
                data-testid="kyb-stepup-code"
                className="h-11 text-center text-lg tracking-[0.3em]"/>
            </div>
          )}

          {stepupFactor === "sms" && (
            <div className="space-y-1.5">
              <Label className="text-[12px]">Code sent to <span className="font-medium">{stepupPhoneMasked || "your phone"}</span></Label>
              <Input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
                value={stepupCode}
                onChange={e => setStepupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••• •••"
                data-testid="kyb-stepup-code"
                className="h-11 text-center text-lg tracking-[0.3em]"/>
              <button type="button" onClick={() => sendSmsForChange()}
                className="text-[11px] text-foreground/55 hover:text-foreground underline"
                data-testid="kyb-resend-sms">Resend SMS</button>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button type="button" variant="outline" className="rounded-full"
              onClick={resetStepup} data-testid="kyb-stepup-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || !stepupCode || !stepupFactor}
              data-testid="kyb-stepup-submit"
              className="rounded-full bg-foreground text-background ml-auto">
              {saving ? "Saving…" : "Save business details"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Payment & Billing ───────────────────────────────────────────────────
// ── Payment & billing ───────────────────────────────────────────────────
const MAX_PAYMENT_METHODS = 10;

// Visual brand colour stamp used on each card row.
const CARD_BRAND_META = {
  visa:       { label: "Visa",       bg: "bg-[#1a1f71]", fg: "text-white" },
  mastercard: { label: "Mastercard", bg: "bg-[#eb001b]", fg: "text-white" },
  amex:       { label: "Amex",       bg: "bg-[#006fcf]", fg: "text-white" },
  discover:   { label: "Discover",   bg: "bg-[#ff6000]", fg: "text-white" },
  other:      { label: "Card",       bg: "bg-foreground", fg: "text-background" },
};

function detectCardBrand(num) {
  const s = (num || "").replace(/\D/g, "");
  if (/^4\d{0,15}$/.test(s)) return "visa";
  if (/^(5[1-5]|2[2-7])\d*$/.test(s)) return "mastercard";
  if (/^3[47]\d*$/.test(s)) return "amex";
  if (/^(6011|65|64[4-9])\d*$/.test(s)) return "discover";
  return "other";
}

function formatExp(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function AddCardForm({ disabled, onAdd }) {
  const empty = {
    number: "", exp: "", cvc: "", name: "", label: "",
    cardholder_name: "",
    billing_address_line1: "", billing_address_line2: "",
    billing_city: "", billing_state: "",
    billing_postal_code: "", billing_country: "US",
    cardholder_authorized: false,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const brand = detectCardBrand(form.number);
  const last4 = form.number.replace(/\D/g, "").slice(-4);
  const valid =
    last4.length === 4 &&
    /^\d{2}\/\d{2}$/.test(form.exp) &&
    !!form.cardholder_name &&
    !!form.billing_address_line1 &&
    !!form.billing_city &&
    !!form.billing_postal_code &&
    !!form.cardholder_authorized;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await onAdd({
        type: "card",
        last4,
        brand,
        exp: form.exp,
        label: form.label || null,
        cardholder_name:        form.cardholder_name,
        billing_address_line1:  form.billing_address_line1,
        billing_address_line2:  form.billing_address_line2 || null,
        billing_city:           form.billing_city,
        billing_state:          form.billing_state || null,
        billing_postal_code:    form.billing_postal_code,
        billing_country:        form.billing_country || "US",
        cardholder_authorized:  true,
      });
      setForm(empty);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-border p-4 space-y-3" data-testid="add-card-form">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="w-4 h-4 text-foreground/55"/>
        <div className="text-[13px] font-semibold">Add a card</div>
        <span className={`ml-auto chip ${CARD_BRAND_META[brand].bg} ${CARD_BRAND_META[brand].fg}`}>
          {CARD_BRAND_META[brand].label}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="sm:col-span-4">
          <Label className="text-[12px] text-foreground/60">Card number</Label>
          <Input
            inputMode="numeric"
            placeholder="4242 4242 4242 4242"
            value={form.number}
            onChange={e => set("number", e.target.value)}
            data-testid="card-number"
            className="font-mono"
            maxLength={23}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[12px] text-foreground/60">Expiry (MM/YY)</Label>
          <Input
            placeholder="12/29"
            value={form.exp}
            onChange={e => set("exp", formatExp(e.target.value))}
            data-testid="card-exp"
            className="font-mono"
            maxLength={5}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[12px] text-foreground/60">CVC <span className="text-foreground/40">(not stored)</span></Label>
          <Input
            inputMode="numeric"
            placeholder="123"
            value={form.cvc}
            onChange={e => set("cvc", e.target.value.replace(/\D/g, "").slice(0, 4))}
            data-testid="card-cvc"
            className="font-mono"
          />
        </div>
        <div className="sm:col-span-4">
          <Label className="text-[12px] text-foreground/60">Name on card</Label>
          <Input
            placeholder="As printed on the card"
            value={form.cardholder_name}
            onChange={e => set("cardholder_name", e.target.value)}
            data-testid="card-name"
            maxLength={80}
          />
        </div>
      </div>

      {/* ── Billing address (AVS / fraud-screen) ─────────────────── */}
      <div className="rounded-lg border border-foreground/15 p-3 space-y-3 bg-white">
        <div className="text-[12.5px] font-semibold">Billing address</div>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <div className="sm:col-span-4">
            <Label className="text-[12px] text-foreground/60">Street</Label>
            <Input
              value={form.billing_address_line1}
              onChange={e => set("billing_address_line1", e.target.value)}
              data-testid="card-addr-1"
              maxLength={120}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-foreground/60">Apt / Suite</Label>
            <Input
              value={form.billing_address_line2}
              onChange={e => set("billing_address_line2", e.target.value)}
              data-testid="card-addr-2"
              maxLength={60}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-foreground/60">City</Label>
            <Input
              value={form.billing_city}
              onChange={e => set("billing_city", e.target.value)}
              data-testid="card-city"
              maxLength={60}
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[12px] text-foreground/60">State</Label>
            <Input
              value={form.billing_state}
              onChange={e => set("billing_state", e.target.value)}
              data-testid="card-state"
              maxLength={20}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-foreground/60">ZIP / Postal</Label>
            <Input
              value={form.billing_postal_code}
              onChange={e => set("billing_postal_code", e.target.value)}
              data-testid="card-zip"
              maxLength={12}
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[12px] text-foreground/60">Country</Label>
            <Input
              value={form.billing_country}
              onChange={e => set("billing_country", e.target.value.toUpperCase().slice(0, 2))}
              data-testid="card-country"
              className="font-mono uppercase"
              maxLength={2}
            />
          </div>
        </div>
      </div>

      <div className="sm:col-span-6">
        <Label className="text-[12px] text-foreground/60">Label (optional)</Label>
        <Input
          placeholder="Ops card"
          value={form.label}
          onChange={e => set("label", e.target.value)}
          data-testid="card-label"
          maxLength={60}
        />
      </div>

      {/* ── Cardholder authorization (required) ──────────────────── */}
      <label
        className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
          form.cardholder_authorized
            ? "border-emerald-500/40 bg-emerald-50/40"
            : "border-foreground/20 bg-secondary/30 hover:border-foreground/40"
        }`}
        data-testid="card-auth-toggle"
      >
        <input
          type="checkbox"
          checked={form.cardholder_authorized}
          onChange={e => set("cardholder_authorized", e.target.checked)}
          data-testid="card-auth-checkbox"
          className="mt-0.5 w-4 h-4 accent-emerald-600 shrink-0"
        />
        <span className="text-[12px] text-foreground/75 leading-snug">
          <strong>I authorize this card.</strong> I confirm I am the cardholder
          (or an authorized user of this card), and I agree not to dispute
          valid transactions charged to this card by this account. This
          authorization is logged with my IP, browser, and the timestamp.
        </span>
      </label>

      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-[11px] text-foreground/50">
          Sandbox-grade — production tokenizes via a PCI gateway before reaching us. Only last4 + brand + expiry + billing zip are stored.
        </p>
        <Button type="submit" disabled={disabled || busy || !valid} data-testid="card-save" className="rounded-full bg-foreground text-background">
          {busy ? "Adding…" : "Add card"}
        </Button>
      </div>
    </form>
  );
}

function AddBankForm({ disabled, onAdd }) {
  const empty = {
    bank_name: "", account_holder: "", account_type: "checking", label: "",
    // ACH
    routing_number: "", account_number: "",
    // Domestic wire
    wire_routing_number: "",
    // International wire
    swift_bic: "", iban: "", bank_country: "US",
    // Bank address
    bank_address_line1: "", bank_address_line2: "",
    bank_city: "", bank_state: "", bank_postal_code: "",
    // Account holder address
    account_holder_address_line1: "", account_holder_address_line2: "",
    account_holder_city: "", account_holder_state: "",
    account_holder_postal_code: "", account_holder_country: "US",
    // Intermediary / compliance
    intermediary_bank_name: "", intermediary_swift_bic: "",
    intermediary_account_number: "",
    currency: "USD", purpose_of_payment: "",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [showWire, setShowWire] = useState(false);
  const [showIntl, setShowIntl] = useState(false);
  const [showAddr, setShowAddr] = useState(false);

  // Each rail's own validity. At least one rail must be valid.
  const achOk  = /^\d{9}$/.test(form.routing_number) && form.account_number.length >= 4;
  const wireOk = /^\d{9}$/.test(form.wire_routing_number) && form.account_number.length >= 4;
  const intlOk = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/i.test(form.swift_bic.replace(/\s/g, "")) &&
                 (form.iban.replace(/\s/g, "").length >= 8 || form.account_number.length >= 4);
  const valid = !!form.bank_name && !!form.account_holder && (achOk || wireOk || intlOk);
  const railsLabel = [
    achOk  && "ACH",
    wireOk && "Wire",
    intlOk && "Intl Wire",
  ].filter(Boolean).join(" + ") || "—";

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDigits = (k, v, max) => set(k, v.replace(/\D/g, "").slice(0, max));

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      // Only forward non-empty fields so the backend doesn't store blanks.
      const payload = { type: "bank" };
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = v;
      });
      if (!form.label) delete payload.label;
      await onAdd(payload);
      setForm(empty);
      setShowWire(false); setShowIntl(false); setShowAddr(false);
    } finally { setBusy(false); }
  };

  const SectionToggle = ({ open, onClick, title, hint, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between rounded-lg border border-foreground/15 bg-secondary/30 hover:bg-secondary/60 px-3 py-2 transition-colors"
    >
      <div className="flex flex-col items-start">
        <span className="text-[12.5px] font-semibold">{title}</span>
        <span className="text-[11px] text-foreground/55">{hint}</span>
      </div>
      <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">
        {open ? "Hide" : "Show"}
      </span>
    </button>
  );

  return (
    <form onSubmit={submit} className="rounded-xl border border-border p-4 space-y-4" data-testid="add-bank-form">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-foreground/55"/>
        <div className="text-[13px] font-semibold">Add a bank account</div>
        <span className="ml-auto chip bg-secondary text-foreground/70" data-testid="bank-rails-summary">{railsLabel}</span>
      </div>

      {/* ── 1. Account identity ────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        <div className="sm:col-span-3">
          <Label className="text-[12px] text-foreground/60">Bank name</Label>
          <Input
            placeholder="Chase, Wells Fargo, …"
            value={form.bank_name}
            onChange={e => set("bank_name", e.target.value)}
            data-testid="bank-name"
            maxLength={80}
          />
        </div>
        <div className="sm:col-span-3">
          <Label className="text-[12px] text-foreground/60">Account holder <span className="text-foreground/40">(legal name)</span></Label>
          <Input
            placeholder="Acme Industries LLC"
            value={form.account_holder}
            onChange={e => set("account_holder", e.target.value)}
            data-testid="bank-holder"
            maxLength={120}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[12px] text-foreground/60">Type</Label>
          <select
            value={form.account_type}
            onChange={e => set("account_type", e.target.value)}
            className="w-full h-10 border border-foreground/15 rounded-md px-2 bg-white text-[14px]"
            data-testid="bank-type"
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
            <option value="business_checking">Business Checking</option>
            <option value="business_savings">Business Savings</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[12px] text-foreground/60">Currency</Label>
          <select
            value={form.currency}
            onChange={e => set("currency", e.target.value)}
            className="w-full h-10 border border-foreground/15 rounded-md px-2 bg-white text-[14px]"
            data-testid="bank-currency"
          >
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
            <option value="CAD">CAD — Canadian Dollar</option>
            <option value="MXN">MXN — Mexican Peso</option>
            <option value="JPY">JPY — Japanese Yen</option>
            <option value="CNY">CNY — Chinese Yuan</option>
            <option value="AUD">AUD — Australian Dollar</option>
            <option value="INR">INR — Indian Rupee</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[12px] text-foreground/60">Bank country</Label>
          <Input
            placeholder="US"
            value={form.bank_country}
            onChange={e => set("bank_country", e.target.value.toUpperCase().slice(0, 2))}
            data-testid="bank-country"
            className="font-mono uppercase"
            maxLength={2}
          />
        </div>
      </div>

      {/* ── 2. ACH (default visible — most common rail) ────────────── */}
      <div className="rounded-lg border border-foreground/15 p-3 space-y-3 bg-white">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-semibold">ACH</span>
          <span className="text-[11px] text-foreground/55">Domestic transfers (1–3 business days)</span>
          {achOk && <span className="ml-auto chip bg-emerald-50 text-emerald-700">Ready</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-foreground/60">ACH routing # <span className="text-foreground/40">(9 digits)</span></Label>
            <Input
              inputMode="numeric"
              placeholder="026073150"
              value={form.routing_number}
              onChange={e => setDigits("routing_number", e.target.value, 9)}
              data-testid="bank-routing"
              className="font-mono"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[12px] text-foreground/60">Account #</Label>
            <Input
              inputMode="numeric"
              placeholder="0123456789"
              value={form.account_number}
              onChange={e => setDigits("account_number", e.target.value, 17)}
              data-testid="bank-account"
              className="font-mono"
            />
          </div>
        </div>
      </div>

      {/* ── 3. Domestic wire — collapsible (often different ABA) ───── */}
      <SectionToggle
        open={showWire}
        onClick={() => setShowWire(v => !v)}
        title="Domestic wire details"
        hint="Faster (same-day), uses a different FedWire ABA than ACH"
        testId="bank-toggle-wire"
      />
      {showWire && (
        <div className="rounded-lg border border-foreground/15 p-3 space-y-3 bg-white">
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900 flex items-start gap-2" data-testid="wire-fraud-warning">
            <span aria-hidden className="font-bold mt-px">⚠️</span>
            <span><strong>Wire-fraud warning.</strong> We will <strong>never</strong> email or message you to change wire-routing instructions. If you receive such a request — even from a known contact — verify it by phone using a number you already trust before saving or sending funds.</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-[12px] text-foreground/60">Wire routing # <span className="text-foreground/40">(9 digits, FedWire)</span></Label>
              <Input
                inputMode="numeric"
                placeholder="021000021"
                value={form.wire_routing_number}
                onChange={e => setDigits("wire_routing_number", e.target.value, 9)}
                data-testid="bank-wire-routing"
                className="font-mono"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[12px] text-foreground/60">Purpose of payment <span className="text-foreground/40">(optional, ≤240)</span></Label>
              <Input
                placeholder="e.g. supplier payment"
                value={form.purpose_of_payment}
                onChange={e => set("purpose_of_payment", e.target.value.slice(0, 240))}
                data-testid="bank-wire-purpose"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── 4. International wire (SWIFT/IBAN) — collapsible ───────── */}
      <SectionToggle
        open={showIntl}
        onClick={() => setShowIntl(v => !v)}
        title="International wire (SWIFT / IBAN)"
        hint="Cross-border payments — adds SWIFT/BIC and (optionally) IBAN"
        testId="bank-toggle-intl"
      />
      {showIntl && (
        <div className="rounded-lg border border-foreground/15 p-3 space-y-3 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-[12px] text-foreground/60">SWIFT / BIC <span className="text-foreground/40">(8 or 11 chars)</span></Label>
              <Input
                placeholder="CHASUS33XXX"
                value={form.swift_bic}
                onChange={e => set("swift_bic", e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 11))}
                data-testid="bank-swift"
                className="font-mono uppercase"
                maxLength={11}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[12px] text-foreground/60">IBAN <span className="text-foreground/40">(optional, ≤34)</span></Label>
              <Input
                placeholder="GB82 WEST 1234 5698 7654 32"
                value={form.iban}
                onChange={e => set("iban", e.target.value.toUpperCase().slice(0, 42))}
                data-testid="bank-iban"
                className="font-mono uppercase"
                maxLength={42}
              />
            </div>
            <div className="sm:col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-foreground/10">
              <div className="sm:col-span-1">
                <Label className="text-[12px] text-foreground/60">Intermediary bank</Label>
                <Input
                  placeholder="(Optional) Correspondent"
                  value={form.intermediary_bank_name}
                  onChange={e => set("intermediary_bank_name", e.target.value)}
                  data-testid="bank-int-name"
                />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-[12px] text-foreground/60">Intermediary SWIFT/BIC</Label>
                <Input
                  placeholder="e.g. CHASGB2L"
                  value={form.intermediary_swift_bic}
                  onChange={e => set("intermediary_swift_bic", e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 11))}
                  data-testid="bank-int-swift"
                  className="font-mono uppercase"
                  maxLength={11}
                />
              </div>
              <div className="sm:col-span-1">
                <Label className="text-[12px] text-foreground/60">Intermediary account #</Label>
                <Input
                  placeholder="(Optional)"
                  value={form.intermediary_account_number}
                  onChange={e => set("intermediary_account_number", e.target.value.replace(/\s/g, "").slice(0, 34))}
                  data-testid="bank-int-account"
                  className="font-mono"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 5. Addresses — collapsible ─────────────────────────────── */}
      <SectionToggle
        open={showAddr}
        onClick={() => setShowAddr(v => !v)}
        title="Bank & beneficiary addresses"
        hint="Required for most wires and many AML compliance regimes"
        testId="bank-toggle-addr"
      />
      {showAddr && (
        <div className="rounded-lg border border-foreground/15 p-3 space-y-4 bg-white">
          <div>
            <div className="text-[12.5px] font-semibold mb-2">Bank address</div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-4">
                <Label className="text-[12px] text-foreground/60">Street</Label>
                <Input value={form.bank_address_line1} onChange={e => set("bank_address_line1", e.target.value)} data-testid="bank-addr-1"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">Suite / Unit</Label>
                <Input value={form.bank_address_line2} onChange={e => set("bank_address_line2", e.target.value)} data-testid="bank-addr-2"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">City</Label>
                <Input value={form.bank_city} onChange={e => set("bank_city", e.target.value)} data-testid="bank-city"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">State / Province</Label>
                <Input value={form.bank_state} onChange={e => set("bank_state", e.target.value)} data-testid="bank-state"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">Postal code</Label>
                <Input value={form.bank_postal_code} onChange={e => set("bank_postal_code", e.target.value)} data-testid="bank-postal"/>
              </div>
            </div>
          </div>
          <div>
            <div className="text-[12.5px] font-semibold mb-2">Beneficiary (account holder) address</div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-4">
                <Label className="text-[12px] text-foreground/60">Street</Label>
                <Input value={form.account_holder_address_line1} onChange={e => set("account_holder_address_line1", e.target.value)} data-testid="holder-addr-1"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">Suite / Unit</Label>
                <Input value={form.account_holder_address_line2} onChange={e => set("account_holder_address_line2", e.target.value)} data-testid="holder-addr-2"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">City</Label>
                <Input value={form.account_holder_city} onChange={e => set("account_holder_city", e.target.value)} data-testid="holder-city"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[12px] text-foreground/60">State / Province</Label>
                <Input value={form.account_holder_state} onChange={e => set("account_holder_state", e.target.value)} data-testid="holder-state"/>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-[12px] text-foreground/60">Postal code</Label>
                <Input value={form.account_holder_postal_code} onChange={e => set("account_holder_postal_code", e.target.value)} data-testid="holder-postal"/>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-[12px] text-foreground/60">Country</Label>
                <Input
                  placeholder="US"
                  value={form.account_holder_country}
                  onChange={e => set("account_holder_country", e.target.value.toUpperCase().slice(0, 2))}
                  data-testid="holder-country"
                  className="font-mono uppercase"
                  maxLength={2}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 6. Footer ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-1">
          <Label className="text-[12px] text-foreground/60">Label (optional)</Label>
          <Input
            placeholder="Primary Ops"
            value={form.label}
            onChange={e => set("label", e.target.value)}
            data-testid="bank-label"
            maxLength={60}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-[11px] text-foreground/50 max-w-[60%]">
          Account & routing numbers are encrypted at rest and never returned by the API after save. We mirror to Modern Treasury for ACH verification when configured.
        </p>
        <Button type="submit" disabled={disabled || busy || !valid} data-testid="bank-save" className="rounded-full bg-foreground text-background">
          {busy ? "Adding…" : "Add bank"}
        </Button>
      </div>
    </form>
  );
}

// ── BankRow — list item with expand-to-reveal sensitive numbers ──────────
// Compact by default (one line). Tapping "Details" reveals a RevealableNumber
// for each rail-relevant field (ACH routing + account, wire routing, SWIFT,
// IBAN). The reveal itself is password-gated (see backend
// /payment-methods/{id}/reveal).
function BankRow({ m, onSetDefault, onRemove }) {
  const [open, setOpen] = useState(false);
  // Decide which fields are *worth* showing based on the rails the user
  // saved at creation time. Stored as a CSV string.
  const rails = (m.rails || "").split(",").map(s => s.trim()).filter(Boolean);
  const fields = [];
  if (rails.includes("ach"))         fields.push({ key: "routing_number",      digits: 9 });
  if (rails.includes("wire"))        fields.push({ key: "wire_routing_number", digits: 9 });
  if (rails.includes("ach") || rails.includes("wire")) fields.push({ key: "account_number", digits: 12 });
  if (rails.includes("intl_wire"))   fields.push({ key: "swift_bic",           digits: 11 });
  if (rails.includes("intl_wire"))   fields.push({ key: "iban",                digits: 24 });
  if (fields.length === 0) {
    fields.push({ key: "routing_number", digits: 9 });
    fields.push({ key: "account_number", digits: 12 });
  }

  return (
    <li className="p-4" data-testid={`pm-${m.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-7 rounded-md grid place-items-center bg-foreground text-background"><Building2 className="w-3.5 h-3.5"/></span>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium truncate">{m.label || `${m.bank_name} ···${m.last4}`}</div>
          <div className="text-[11.5px] text-foreground/55 font-mono">
            {m.bank_name} ···{m.last4} · {m.account_type || "checking"}
            {m.currency && m.currency !== "USD" ? ` · ${m.currency}` : ""}
            {m.bank_country && m.bank_country !== "US" ? ` · ${m.bank_country}` : ""}
            {m.verified ? " · verified" : ""}
          </div>
          {m.rails && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`pm-rails-${m.id}`}>
              {rails.map(r => {
                const labelMap = { ach: "ACH", wire: "Wire", intl_wire: "Intl Wire" };
                return (
                  <span key={r} className="chip bg-secondary text-foreground/70 text-[10px]">
                    {labelMap[r] || r.toUpperCase()}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        {fields.length > 0 && (
          <Button
            size="sm" variant="outline"
            onClick={() => setOpen(o => !o)}
            data-testid={`pm-toggle-${m.id}`}
            className="rounded-full"
          >
            {open ? "Hide details" : "Details"}
          </Button>
        )}
        {m.is_default ? (
          <span className="chip bg-accent/20 text-foreground" data-testid={`pm-default-${m.id}`}>Default</span>
        ) : (
          <Button size="sm" variant="outline" onClick={onSetDefault} data-testid={`pm-set-default-${m.id}`}>Set default</Button>
        )}
        <Button size="sm" variant="ghost" onClick={onRemove} data-testid={`pm-remove-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-foreground/50"/></Button>
      </div>
      {open && fields.length > 0 && (
        <div className="mt-3 ml-12 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border" data-testid={`pm-reveal-${m.id}`}>
          {fields.map(f => (
            <RevealableNumber
              key={f.key}
              methodId={m.id}
              field={f.key}
              last4={m.last4}
              digitsHint={f.digits}
              testIdPrefix={`pm-${m.id}`}
            />
          ))}
        </div>
      )}
    </li>
  );
}

export function PaymentBillingPanel() {
  const [methods, setMethods] = useState([]);
  const [plaidStatus, setPlaidStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("card"); // 'card' | 'bank'

  const load = async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/payment-methods"),
        api.get("/onboarding/plaid/summary").catch(() => ({ data: null })),
      ]);
      setMethods(m.data || []);
      setPlaidStatus(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addMethod = async (payload) => {
    try {
      await api.post("/payment-methods", payload);
      toast.success(payload.type === "card" ? "Card added" : "Bank account added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add payment method");
      throw e;
    }
  };

  const removeMethod = async (id) => {
    if (!confirm("Remove this payment method?")) return;
    try {
      await api.delete(`/payment-methods/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/payment-methods/${id}/default`);
      toast.success("Set as default");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const cards = methods.filter(m => m.type === "card");
  const banks = methods.filter(m => m.type === "bank");
  const atCap = methods.length >= MAX_PAYMENT_METHODS;

  return (
    <div>
      <SectionHeader title="Payment & billing" desc="Bank accounts and cards used for wallet top-ups, payouts, and invoice settlement. Up to 10 methods per account."/>

      {/* Plaid quick-connect */}
      <div className="rounded-xl border border-border bg-secondary/30 p-5 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-foreground text-accent grid place-items-center">
            <Sparkles className="w-5 h-5"/>
          </span>
          <div className="flex-1">
            <div className="display text-lg">Plaid quick-connect</div>
            <div className="text-[12.5px] text-foreground/60">{plaidStatus?.item_id ? "Connected — verified balance + transactions" : "One-click connect to 10,000+ US banks (auto-verifies ACH)"}</div>
          </div>
          {plaidStatus?.item_id ? (
            <span className="chip bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"><Check className="w-3 h-3"/>Connected</span>
          ) : (
            <Button size="sm" className="rounded-full bg-foreground text-background" onClick={() => window.location.href = "/buyer/payments"} data-testid="plaid-connect">Connect</Button>
          )}
        </div>
      </div>

      {/* Add new method — segmented control */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <SectionHeader title="Add a new method"/>
          <span className={`chip ${atCap ? "bg-rose-500/15 text-rose-700 border border-rose-500/30" : "bg-secondary text-foreground/70"}`} data-testid="pm-cap-chip">
            {methods.length} / {MAX_PAYMENT_METHODS} used
          </span>
        </div>
        {atCap ? (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-[13px] text-rose-900" data-testid="pm-cap-banner">
            You've hit the {MAX_PAYMENT_METHODS}-method limit. Remove one below before adding another.
          </div>
        ) : (
          <>
            <div className="inline-flex rounded-full border border-border p-1 mb-3" data-testid="pm-tabs">
              <button
                type="button"
                onClick={() => setTab("card")}
                className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium transition ${tab === "card" ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
                data-testid="pm-tab-card"
              >
                <CreditCard className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5"/>Card
              </button>
              <button
                type="button"
                onClick={() => setTab("bank")}
                className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium transition ${tab === "bank" ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
                data-testid="pm-tab-bank"
              >
                <Building2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5"/>Bank account
              </button>
            </div>
            {tab === "card" ? (
              <AddCardForm disabled={atCap} onAdd={addMethod}/>
            ) : (
              <AddBankForm disabled={atCap} onAdd={addMethod}/>
            )}
          </>
        )}
      </div>

      {/* Saved methods */}
      <SectionHeader title="Saved payment methods"/>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>
      ) : methods.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center text-[13px] text-foreground/55">
          No saved methods yet. Add a card or bank above, or connect via Plaid.
        </div>
      ) : (
        <div className="space-y-5">
          {cards.length > 0 && (
            <div>
              <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">Cards</div>
              <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                {cards.map(m => {
                  const meta = CARD_BRAND_META[(m.brand || "other").toLowerCase()] || CARD_BRAND_META.other;
                  return (
                    <li key={m.id} className="p-4 flex items-center gap-3" data-testid={`pm-${m.id}`}>
                      <span className={`w-10 h-7 rounded-md grid place-items-center text-[10px] font-bold tracking-wide ${meta.bg} ${meta.fg}`}>{meta.label.toUpperCase()}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-medium truncate">{m.label || `•••• ${m.last4}`}</div>
                        <div className="text-[11.5px] text-foreground/55 font-mono">•••• {m.last4}{m.exp ? ` · exp ${m.exp}` : ""}</div>
                      </div>
                      {m.is_default ? (
                        <span className="chip bg-accent/20 text-foreground" data-testid={`pm-default-${m.id}`}>Default</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setDefault(m.id)} data-testid={`pm-set-default-${m.id}`}>Set default</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => removeMethod(m.id)} data-testid={`pm-remove-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-foreground/50"/></Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {banks.length > 0 && (
            <div>
              <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">Bank accounts</div>
              <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                {banks.map(m => (
                  <BankRow
                    key={m.id}
                    m={m}
                    onSetDefault={() => setDefault(m.id)}
                    onRemove={() => removeMethod(m.id)}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shipping addresses ──────────────────────────────────────────────────
export function ShippingAddressesPanel({ labelSingular = "address", titleLabel = "Shipping addresses", descLabel }) {
  const [addrs, setAddrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ label: "", line1: "", city: "", state: "", zip: "", country: "US" });

  const load = async () => {
    try {
      const { data } = await api.get("/addresses");
      setAddrs(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (addrs.length >= 5) return toast.error("Maximum 5 addresses");
    try {
      await api.post("/addresses", form);
      setShow(false);
      setForm({ label: "", line1: "", city: "", state: "", zip: "", country: "US" });
      load();
      toast.success("Address added");
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this address?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div>
      <SectionHeader title={titleLabel} desc={descLabel || `Default ${labelSingular} and any additional ones — used on orders and invoices.`} action={
        <Button onClick={() => setShow(v => !v)} data-testid="addr-toggle" className="rounded-full bg-foreground text-background"><Plus className="w-4 h-4 mr-1"/>Add {labelSingular}</Button>
      }/>
      {show && (
        <form onSubmit={add} className="mb-5 rounded-xl border border-border p-4 bg-secondary/30 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Label (e.g. Main warehouse)" value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="addr-label"/>
            <Input placeholder="Country" value={form.country} onChange={e => setForm({...form, country: e.target.value})}/>
          </div>
          <Input placeholder="Street address" value={form.line1} onChange={e => setForm({...form, line1: e.target.value})} required data-testid="addr-line1"/>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} required/>
            <Input placeholder="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} required/>
            <Input placeholder="ZIP" value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} required/>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" data-testid="addr-save">Save</Button>
          </div>
        </form>
      )}
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/> : addrs.length === 0 ? (
        <div className="text-[13px] text-foreground/55 py-6">No {labelSingular}s saved yet.</div>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
          {addrs.map((a, i) => (
            <li key={a.id} className="p-4 flex items-start gap-3" data-testid={`addr-${a.id}`}>
              <Building2 className="w-4 h-4 text-foreground/60 mt-0.5"/>
              <div className="flex-1">
                <div className="text-[14px] font-medium">{a.label || `Address ${i + 1}`} {i === 0 && <span className="chip ml-2 bg-accent/20 text-foreground">Default</span>}</div>
                <div className="text-[12.5px] text-foreground/60 mt-0.5">{a.line1}, {a.city}, {a.state} {a.zip}, {a.country}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(a.id)}><Trash2 className="w-3.5 h-3.5 text-foreground/50"/></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Notifications ───────────────────────────────────────────────────────
// Role-aware, grouped notification matrix. Schema (groups + items + which
// SMS rows are even supported) comes from the backend taxonomy so adding a
// new event is a single backend edit. This component only renders.
export function NotificationsPanel() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tx, pf] = await Promise.all([
          api.get("/notifications/taxonomy"),
          api.get("/notifications/preferences"),
        ]);
        if (!alive) return;
        setTaxonomy(tx.data);
        setPrefs(pf.data);
      } catch {
        toast.error("Couldn't load notification preferences");
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = async (channel, key) => {
    const next = { ...prefs, [channel]: { ...prefs[channel], [key]: !prefs[channel]?.[key] } };
    setPrefs(next);
    setSaving(true);
    try {
      await api.patch("/notifications/preferences", { [channel]: { [key]: next[channel][key] } });
    } catch {
      // Roll back optimistic toggle on failure.
      setPrefs(prefs);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!taxonomy || !prefs) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  const smsConfigured = !!taxonomy.channels?.sms?.configured;
  const smsPhoneOk    = !!taxonomy.channels?.sms?.phone_verified;
  const emailConfigured = !!taxonomy.channels?.email?.configured;

  return (
    <div>
      <SectionHeader
        title="Notification preferences"
        desc="Choose how we reach you for every event. Email goes out via Resend, SMS via Twilio — both honour these toggles before sending."
        action={saving && <span className="text-[11.5px] text-foreground/50 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin"/>Saving…</span>}
      />

      {/* Channel status banners — make it obvious when a channel is disabled platform-wide */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="notif-channel-banners">
        <span className={`chip ${emailConfigured ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" : "bg-amber-500/15 text-amber-700 border border-amber-500/30"}`}>
          <Mail className="w-3 h-3"/>{emailConfigured ? "Email: live (Resend)" : "Email: not configured"}
        </span>
        <span className={`chip ${smsConfigured ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" : "bg-amber-500/15 text-amber-700 border border-amber-500/30"}`}>
          <Phone className="w-3 h-3"/>{smsConfigured ? (smsPhoneOk ? "SMS: live (Twilio)" : "SMS: live · verify your phone") : "SMS: not configured"}
        </span>
      </div>

      <div className="space-y-5">
        {taxonomy.groups.map(g => (
          <div key={g.id} className="rounded-xl border border-border overflow-hidden" data-testid={`notif-group-${g.id}`}>
            <div className="px-4 py-3 bg-secondary/40 border-b border-border">
              <div className="text-[13px] font-semibold">{g.label}</div>
              {g.desc && <div className="text-[11.5px] text-foreground/55 mt-0.5">{g.desc}</div>}
            </div>
            <table className="w-full text-[13.5px]">
              <thead className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2.5 font-normal">Event</th>
                  <th className="text-center px-4 py-2.5 font-normal w-20">Email</th>
                  <th className="text-center px-4 py-2.5 font-normal w-20">SMS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {g.items.map(it => {
                  const emailOn = !!prefs.email?.[it.key];
                  const smsOn   = !!prefs.sms?.[it.key];
                  return (
                    <tr key={it.key} data-testid={`notif-row-${it.key}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{it.label}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={emailOn}
                          onCheckedChange={() => toggle("email", it.key)}
                          disabled={!emailConfigured}
                          data-testid={`notif-email-${it.key}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {it.sms_supported ? (
                          <Switch
                            checked={smsOn}
                            onCheckedChange={() => toggle("sms", it.key)}
                            disabled={!smsConfigured}
                            data-testid={`notif-sms-${it.key}`}
                          />
                        ) : (
                          <span className="text-foreground/30">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <p className="text-[11.5px] text-foreground/50 mt-4">
        SMS requires a verified phone (Settings → Security) and Twilio Messages enabled by your platform admin.
        Emails are automatically branded with your seller's name and link back to their portal.
      </p>
    </div>
  );
}

// ── Team Members ────────────────────────────────────────────────────────
export function TeamMembersPanel() {
  const [state, setState] = useState({ roles: {}, members: [] });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer" });
  const [scopeMember, setScopeMember] = useState(null); // member row when editing buyer scope

  const load = () => api.get("/team").then(r => setState(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", form);
      setForm({ email: "", name: "", role: "viewer" });
      setShow(false);
      load();
      toast.success("Invited");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateRole = async (id, role) => { await api.patch(`/team/${id}`, { role }); load(); };
  const remove = async (id) => { if (confirm("Remove this member?")) { await api.delete(`/team/${id}`); load(); } };
  const resend = async (m) => {
    try {
      await api.post(`/team/${m.id}/resend`);
      toast.success(`Invite re-sent to ${m.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend"); }
  };

  const _emailChip = (m) => {
    const st = m.email_status;
    if (!st || st === "queued") return null;
    if (st === "sent") return <span className="text-[10px] mono uppercase tracking-wider text-emerald-700">email delivered</span>;
    if (st === "skipped_unconfigured") return <span className="text-[10px] mono uppercase tracking-wider text-foreground/40">email skipped</span>;
    return <span className="text-[10px] mono uppercase tracking-wider text-rose-700" title={st}>email failed</span>;
  };

  // Compact "Buyer access" chip in the table — only meaningful for operator rows.
  const _scopeChip = (m) => {
    if (m.role !== "operator") return null;
    const mode = m.buyer_scope?.mode || "all";
    const count = (m.buyer_scope?.buyer_ids || []).length;
    if (mode === "all") {
      return (
        <button onClick={() => setScopeMember(m)} data-testid={`scope-chip-${m.id}`}
          className="text-[10.5px] mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-foreground/20 text-foreground/55 hover:border-foreground/40 transition-colors">
          all buyers · edit
        </button>
      );
    }
    return (
      <button onClick={() => setScopeMember(m)} data-testid={`scope-chip-${m.id}`}
        className="text-[10.5px] mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors">
        {count} buyer{count === 1 ? "" : "s"} · restricted
      </button>
    );
  };

  const roles = Object.entries(state.roles || {});

  return (
    <div>
      <SectionHeader title="Team members" desc="Invite up to 10 people and pick what each one can access. Every action is logged to Security → Audit log." action={
        <Button onClick={() => setShow(v => !v)} data-testid="team-invite" className="rounded-full bg-foreground text-background"><Plus className="w-4 h-4 mr-1"/>Invite member</Button>
      }/>
      {show && (
        <form onSubmit={add} className="mb-5 rounded-xl border border-border p-4 bg-secondary/30 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/>
            <Input placeholder="email@company.com" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="team-email"/>
          </div>
          <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full h-10 border border-foreground/15 rounded-md px-2 bg-white text-[14px]" data-testid="team-role">
            {roles.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" data-testid="team-save">Send invite</Button>
          </div>
        </form>
      )}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
            <tr><th className="text-left p-3">Member</th><th className="text-left p-3">Role</th><th className="text-left p-3">Access</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {state.members.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-foreground/55 text-[13px]">No team members yet. You're working solo for now.</td></tr>
            ) : state.members.map(m => (
              <tr key={m.id} data-testid={`team-row-${m.id}`}>
                <td className="p-3">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-[11.5px] text-foreground/55">{m.email}</div>
                </td>
                <td className="p-3">
                  <select value={m.role} onChange={e => updateRole(m.id, e.target.value)} className="border border-foreground/15 rounded px-2 py-1 text-[12.5px] bg-white">
                    {roles.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  {_scopeChip(m) || <span className="text-[11px] text-foreground/40">—</span>}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`chip ${m.status === "active" ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" : "bg-amber-500/15 text-amber-700 border border-amber-500/30"}`}>{m.status}</span>
                    {_emailChip(m)}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {m.status !== "active" && (
                      <Button size="sm" variant="ghost" onClick={() => resend(m)} title="Re-send invite email" data-testid={`team-resend-${m.id}`}>
                        <Send className="w-3.5 h-3.5 text-foreground/55"/>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)}><Trash2 className="w-3.5 h-3.5 text-foreground/50"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 rounded-xl border border-border bg-secondary/30 p-4">
        <div className="text-[12.5px] font-medium mb-2">Role capabilities</div>
        <ul className="text-[12px] text-foreground/65 space-y-1.5">
          {roles.map(([k, v]) => {
            // Backend ships `{label, desc, can_money, can_write, can_invite,
            // can_banking, can_settings}` — build a human-readable capability
            // list from the flags so we never crash on a missing field.
            const caps = [
              v.can_write    && "edit orders",
              v.can_money    && "pay invoices & move money",
              v.can_banking  && "manage banking & payouts",
              v.can_settings && "change account settings",
              v.can_invite   && "invite team",
              !v.can_write   && "read-only",
            ].filter(Boolean);
            return (
              <li key={k} data-testid={`role-row-${k}`}>
                <code className="mono font-medium">{v.label}</code>
                <span className="text-foreground/55"> — {v.desc || caps.join(" · ")}</span>
              </li>
            );
          })}
        </ul>
        <div className="text-[11.5px] text-foreground/55 mt-3 pt-3 border-t border-border flex items-start gap-1.5">
          <Users className="w-3 h-3 mt-0.5 flex-shrink-0"/>
          <span>Operators can be scoped to a subset of buyers. Click the <strong>access chip</strong> on an Operator row to limit which buyers they can see across Buyers, Orders, Invoices and Claims.</span>
        </div>
      </div>

      <BuyerScopeDialog
        member={scopeMember}
        onClose={() => setScopeMember(null)}
        onSaved={() => { setScopeMember(null); load(); }}
      />
    </div>
  );
}


// ── Per-operator Buyer Access dialog ────────────────────────────────────
function BuyerScopeDialog({ member, onClose, onSaved }) {
  const [data, setData]       = useState(null);   // { mode, buyer_ids, available_buyers }
  const [mode, setMode]       = useState("all");
  const [picked, setPicked]   = useState(() => new Set());
  const [query, setQuery]     = useState("");
  const [saving, setSaving]   = useState(false);

  const open = !!member;

  useEffect(() => {
    if (!open) return;
    api.get(`/team/${member.id}/buyer-scope`)
      .then(r => {
        setData(r.data);
        setMode(r.data.mode || "all");
        setPicked(new Set(r.data.buyer_ids || []));
      })
      .catch(() => toast.error("Could not load buyer access settings"));
  }, [open, member?.id]);

  const filtered = (data?.available_buyers || []).filter(b => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (b.company || "").toLowerCase().includes(q) ||
           (b.name || "").toLowerCase().includes(q) ||
           (b.email || "").toLowerCase().includes(q);
  });

  const toggle = (id) => setPicked(p => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async () => {
    if (mode === "restricted" && picked.size === 0) {
      if (!window.confirm("This operator will have access to ZERO buyers and won't be able to see Buyers, Orders, Invoices or Claims. Save anyway?")) return;
    }
    setSaving(true);
    try {
      await api.put(`/team/${member.id}/buyer-scope`, {
        mode,
        buyer_ids: mode === "restricted" ? Array.from(picked) : [],
      });
      toast.success(mode === "all"
        ? `${member.name || member.email} now has access to all buyers`
        : `${member.name || member.email} restricted to ${picked.size} buyer${picked.size === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="buyer-scope-dialog">
        <DialogHeader>
          <DialogTitle>Buyer access · {member?.name || member?.email}</DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-12 flex items-center justify-center text-foreground/50">
            <Loader2 className="w-4 h-4 animate-spin mr-2"/> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[13px] text-foreground/65">
              Restrict this operator to a subset of your connected buyers. They'll only see Buyers,
              Orders, Invoices and Claims for the buyers you pick.
            </p>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("all")}
                data-testid="scope-mode-all"
                className={`text-left p-3.5 rounded-xl border-2 transition-colors ${
                  mode === "all"
                    ? "border-foreground bg-foreground/5"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <div className="font-medium text-[14px]">All buyers</div>
                <div className="text-[11.5px] text-foreground/55 mt-0.5">Full access (default)</div>
              </button>
              <button
                type="button"
                onClick={() => setMode("restricted")}
                data-testid="scope-mode-restricted"
                className={`text-left p-3.5 rounded-xl border-2 transition-colors ${
                  mode === "restricted"
                    ? "border-foreground bg-foreground/5"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <div className="font-medium text-[14px]">Specific buyers</div>
                <div className="text-[11.5px] text-foreground/55 mt-0.5">Pick the buyers below</div>
              </button>
            </div>

            {mode === "restricted" && (
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="p-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                  <Search className="w-3.5 h-3.5 text-foreground/40"/>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search buyers…"
                    data-testid="scope-search"
                    className="flex-1 bg-transparent outline-none text-[13px]"
                  />
                  <span className="text-[11px] mono uppercase tracking-wider text-foreground/50">
                    {picked.size} / {data.available_buyers.length} picked
                  </span>
                </div>
                <div className="max-h-72 overflow-auto divide-y divide-border">
                  {filtered.length === 0 ? (
                    <div className="p-6 text-center text-[12.5px] text-foreground/55">
                      {data.available_buyers.length === 0
                        ? "You haven't connected any buyers yet."
                        : "No matches"}
                    </div>
                  ) : filtered.map(b => (
                    <label
                      key={b.id}
                      data-testid={`scope-buyer-${b.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-secondary/30 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={picked.has(b.id)}
                        onChange={() => toggle(b.id)}
                        className="w-4 h-4 cursor-pointer accent-foreground"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-medium truncate">{b.company || b.name || b.email}</div>
                        <div className="text-[11px] text-foreground/55 truncate">{b.name && b.company ? `${b.name} · ` : ""}{b.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="scope-save"
                className="rounded-full bg-foreground text-background">
                {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/>Saving…</> : "Save access"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Trusted devices ─────────────────────────────────────────────────────
// Browsers + IP-subnets + countries that have already passed SMS within
// the TTL window (14 days for buyer/seller, 6 hours for admins). Subsequent
// logins from a trusted device skip the SMS challenge entirely. Any IP /
// country / browser change auto-revokes the trust and forces a fresh OTP.
export function TrustedDevicesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ ttl_days_user: 14, ttl_hours_admin: 6 });
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/trusted-devices");
      setRows(data.rows || []);
      setMeta({
        ttl_days_user:   data.ttl_days_user   ?? 14,
        ttl_hours_admin: data.ttl_hours_admin ?? 6,
      });
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revokeOne = async (id) => {
    setActing(id);
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success("Device revoked. Next login from there will require SMS again.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke device");
    } finally { setActing(null); }
  };

  const revokeAll = async () => {
    if (!window.confirm("Revoke trust on every other device? They'll all need SMS verification on their next login.")) return;
    setActing("all");
    try {
      const { data } = await api.post("/auth/trusted-devices/revoke-all");
      toast.success(`${data.revoked} device${data.revoked === 1 ? "" : "s"} revoked.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke devices");
    } finally { setActing(null); }
  };

  const fmtUntil = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const ms = d.getTime() - now.getTime();
    if (ms <= 0) return "Expired";
    const hrs = Math.round(ms / 3600000);
    if (hrs < 24) return `Expires in ${hrs}h`;
    return `Expires in ${Math.round(hrs / 24)}d`;
  };

  const ttlBlurb = `Trusted devices skip the SMS code on login for ${meta.ttl_days_user} days (6 hours for admins). Any change in IP, country, or browser auto-revokes trust.`;

  return (
    <div>
      <SectionHeader
        title="Trusted devices"
        desc={ttlBlurb}
        action={rows.filter(r => !r.current).length > 0 && (
          <Button
            size="sm" variant="outline" className="rounded-full text-[12px] h-8"
            onClick={revokeAll}
            disabled={acting === "all"}
            data-testid="trusted-devices-revoke-all"
          >
            {acting === "all" ? "Revoking…" : "Revoke all other devices"}
          </Button>
        )}
      />
      <div className="rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-4 text-[13px] text-foreground/55 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-[13px] text-foreground/55">
            No trusted devices yet. After your next SMS verification, this browser will appear here.
          </div>
        ) : rows.map(r => (
          <div
            key={r.id}
            className="p-3.5 flex items-center gap-3 text-[13.5px] border-b border-border last:border-b-0"
            data-testid={`trusted-device-${r.id}`}
          >
            <Laptop className="w-4 h-4 text-foreground/60"/>
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                <span className="truncate">{r.name || r.ua_summary || "Unknown device"}</span>
                {r.current && <span className="chip bg-accent/20 text-foreground">Current</span>}
                {r.role === "admin" && <span className="chip bg-amber-100 text-amber-900">Admin</span>}
              </div>
              <div className="text-[11.5px] text-foreground/55 mt-0.5 flex items-center gap-2 flex-wrap">
                <MapPin className="w-3 h-3"/>
                <span>{r.country || "Location unknown"}{r.ip && r.ip !== "—" ? ` · ${r.ip}` : ""}</span>
                <span>· Last seen {new Date(r.last_seen_at).toLocaleString()}</span>
                <span>· {fmtUntil(r.trusted_until)}</span>
              </div>
            </div>
            {!r.current && (
              <Button
                size="sm" variant="outline" className="rounded-full text-[11px] h-7"
                onClick={() => revokeOne(r.id)}
                disabled={acting === r.id}
                data-testid={`trusted-device-revoke-${r.id}`}
              >
                {acting === r.id ? "…" : "Revoke"}
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11.5px] text-foreground/55 mt-2.5 flex items-start gap-1.5">
        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0"/>
        Wallet top-ups, withdrawals, refunds, and invoice adjustments always require a fresh SMS code — trusted-device status only applies to login.
      </p>
    </div>
  );
}


// ── Security ────────────────────────────────────────────────────────────
export function SecurityPanel() {
  // Password change is now a 2-step flow (Jun 2026):
  //   1. user enters current + new → click "Continue"
  //   2. we ask which factor (TOTP or SMS), they enter a code → submit
  // Step 2 also sends the SMS automatically if they picked that factor.
  const [pw, setPw] = useState({ current: "", next: "" });
  const [stepupStage, setStepupStage] = useState("idle");   // idle | choose | enter
  const [stepupFactor, setStepupFactor] = useState(null);   // "totp" | "sms"
  const [stepupCode, setStepupCode] = useState("");
  const [stepupAvailable, setStepupAvailable] = useState([]);
  const [stepupPhoneMasked, setStepupPhoneMasked] = useState(null);
  const [stepupSetupPath, setStepupSetupPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [revoking, setRevoking] = useState(null);

  const loadSessions = () => api.get("/security/sessions").then(r => setSessions(r.data || []));

  useEffect(() => {
    loadSessions();
    api.get("/security/audit-log").then(r => setAudit(r.data || []));
  }, []);

  const resetPwFlow = () => {
    setPw({ current: "", next: "" });
    setStepupStage("idle");
    setStepupFactor(null);
    setStepupCode("");
    setStepupAvailable([]);
    setStepupPhoneMasked(null);
    setStepupSetupPath(null);
  };

  // Step 1 — submit current + new, server tells us which 2FA factors apply.
  const startPasswordChange = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw.next === pw.current) return toast.error("Pick a different new password");
    setSaving(true);
    try {
      // Send WITHOUT a verify_code so backend returns the factor menu.
      await api.post("/security/password", { current: pw.current, new: pw.next });
      // Shouldn't happen — backend should require step-up. If we land here
      // it means dev-bypass was set. Treat as success.
      toast.success("Password changed");
      resetPwFlow();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "stepup_required") {
        // Server is telling us which factors are available.
        setStepupAvailable(detail.available_factors || []);
        setStepupPhoneMasked(detail.phone_masked || null);
        const preferred = (detail.available_factors || [])[0] || null;
        setStepupFactor(preferred);
        setStepupStage("choose");
        // If SMS is the only option, kick off send immediately.
        if (preferred === "sms" && (detail.available_factors || []).length === 1) {
          await sendSmsForChange(true);
        }
      } else if (detail?.code === "mfa_required_first") {
        toast.error(detail.message);
        if (detail.setup_path) setStepupSetupPath(detail.setup_path);
      } else if (detail?.code === "current_password_wrong") {
        toast.error("Current password is incorrect");
      } else if (detail?.code === "password_unchanged") {
        toast.error("Pick a different new password");
      } else if (detail?.code === "weak_password") {
        toast.error("New password must be at least 8 characters");
      } else {
        toast.error(detail?.message || err.message || "Could not change password");
      }
    } finally { setSaving(false); }
  };

  // SMS path — request the OTP from the backend.
  const sendSmsForChange = async (silent = false) => {
    try {
      const { data } = await api.post("/sms/stepup/send", { action: "change_password" });
      if (!silent) toast.success(`Code sent to ${data?.masked || "your phone"}`);
      setStepupStage("enter");
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Could not send SMS code");
    }
  };

  // Step 2 — submit the verify code along with current + new.
  const confirmPasswordChange = async (e) => {
    e?.preventDefault?.();
    if (!stepupFactor || !stepupCode) return;
    setSaving(true);
    try {
      await api.post("/security/password", {
        current:     pw.current,
        new:         pw.next,
        factor:      stepupFactor,
        verify_code: stepupCode.trim(),
      });
      toast.success("Password changed — all other sessions signed out");
      resetPwFlow();
      // Sessions list likely changed (current device kept, others wiped).
      try { await loadSessions(); } catch {}
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || err.message || "Verification failed");
    } finally { setSaving(false); }
  };

  const revokeOne = async (sid) => {
    setRevoking(sid);
    try {
      await api.delete(`/security/sessions/${sid}`);
      toast.success("Device signed out");
      await loadSessions();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign out device"); }
    finally { setRevoking(null); }
  };

  const revokeAllOthers = async () => {
    setRevoking("all");
    try {
      const { data } = await api.delete("/security/sessions/all-others");
      toast.success(`Signed out ${data.revoked} other device${data.revoked === 1 ? "" : "s"}`);
      await loadSessions();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign out devices"); }
    finally { setRevoking(null); }
  };

  return (
    <div className="space-y-8">
      {/* Password */}
      <div>
        <SectionHeader
          title="Password"
          desc="Minimum 8 characters. We require a 2-factor code (TOTP or SMS) to confirm — closes the 'stolen session can change my password' gap."
        />

        {stepupStage === "idle" && (
          <form onSubmit={startPasswordChange} className="max-w-md space-y-3" data-testid="sec-pw-form">
            <Input type="password" placeholder="Current password"
                   value={pw.current}
                   onChange={e => setPw({ ...pw, current: e.target.value })}
                   required data-testid="sec-current-pw"/>
            <Input type="password" placeholder="New password (8+ chars)"
                   value={pw.next}
                   onChange={e => setPw({ ...pw, next: e.target.value })}
                   required minLength={8} data-testid="sec-new-pw"/>
            <Button type="submit" disabled={saving} data-testid="sec-change-pw"
                    className="rounded-full bg-foreground text-background">
              <KeyRound className="w-3.5 h-3.5 mr-1.5"/>
              {saving ? "Checking…" : "Continue"}
            </Button>
            {stepupSetupPath && (
              <div className="text-[12px] text-amber-700 inline-flex items-center gap-1.5 mt-1">
                <AlertCircle className="w-3 h-3"/>
                Set up MFA first → <a className="underline" href={stepupSetupPath}>Settings → Security</a>
              </div>
            )}
          </form>
        )}

        {(stepupStage === "choose" || stepupStage === "enter") && (
          <form onSubmit={confirmPasswordChange} className="max-w-md space-y-4 rounded-xl bg-secondary/40 border border-border p-4" data-testid="sec-pw-stepup">
            <div className="flex items-center gap-2 text-[12px] mono uppercase tracking-[0.18em] text-foreground/55">
              <ShieldCheck className="w-3.5 h-3.5"/>
              Confirm with 2-factor
            </div>
            <p className="text-[12.5px] text-foreground/70 leading-relaxed">
              You're about to change your password. To protect against account
              takeover, enter a code from your authenticator app
              {stepupAvailable.includes("sms") ? " or a Twilio SMS code" : ""}.
            </p>

            {/* Factor toggle (only when BOTH are available) */}
            {stepupAvailable.length > 1 && (
              <div className="flex items-center gap-1.5 bg-background/60 rounded-full p-1 w-fit">
                {stepupAvailable.includes("totp") && (
                  <button type="button"
                    onClick={() => { setStepupFactor("totp"); setStepupStage("enter"); }}
                    data-testid="sec-factor-totp"
                    className={`text-[12px] px-3 py-1 rounded-full ${
                      stepupFactor === "totp"
                        ? "bg-foreground text-background"
                        : "text-foreground/65"
                    }`}>
                    Authenticator
                  </button>
                )}
                {stepupAvailable.includes("sms") && (
                  <button type="button"
                    onClick={() => { setStepupFactor("sms"); sendSmsForChange(); }}
                    data-testid="sec-factor-sms"
                    className={`text-[12px] px-3 py-1 rounded-full ${
                      stepupFactor === "sms"
                        ? "bg-foreground text-background"
                        : "text-foreground/65"
                    }`}>
                    SMS code
                  </button>
                )}
              </div>
            )}

            {stepupFactor === "totp" && (
              <div className="space-y-1.5">
                <Label className="text-[12px]">6-digit code from your authenticator app</Label>
                <Input
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  maxLength={10} autoFocus
                  value={stepupCode}
                  onChange={e => setStepupCode(e.target.value)}
                  placeholder="123 456 (or backup code)"
                  data-testid="sec-stepup-code"
                  className="h-11 text-center text-lg tracking-[0.3em]"
                />
              </div>
            )}

            {stepupFactor === "sms" && (
              <div className="space-y-1.5">
                <Label className="text-[12px]">
                  Code sent to{" "}
                  <span className="font-medium">{stepupPhoneMasked || "your phone"}</span>
                </Label>
                <Input
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  maxLength={6} autoFocus
                  value={stepupCode}
                  onChange={e => setStepupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••• •••"
                  data-testid="sec-stepup-code"
                  className="h-11 text-center text-lg tracking-[0.3em]"
                />
                <button type="button" onClick={() => sendSmsForChange()}
                  className="text-[11px] text-foreground/55 hover:text-foreground underline"
                  data-testid="sec-resend-sms">
                  Resend SMS
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button type="button" variant="outline" className="rounded-full"
                onClick={resetPwFlow} data-testid="sec-stepup-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !stepupCode || !stepupFactor}
                data-testid="sec-stepup-submit"
                className="rounded-full bg-foreground text-background ml-auto">
                {saving ? "Changing…" : "Change password"}
              </Button>
            </div>
            <p className="text-[11px] text-foreground/55">
              After confirming, all other sessions sign out and trusted devices reset.
            </p>
          </form>
        )}
      </div>

      {/* 2FA */}
      <TwoFactorPanel/>

      {/* Trusted devices (Feb 2026 — skip SMS on familiar browsers) */}
      <TrustedDevicesPanel/>

      {/* Sessions */}
      <div>
        <SectionHeader
          title="Active sessions"
          desc="Devices currently signed in to your account."
          action={sessions.filter(s => !s.current).length > 0 && (
            <Button
              size="sm" variant="outline" className="rounded-full text-[12px] h-8"
              onClick={revokeAllOthers}
              disabled={revoking === "all"}
              data-testid="sessions-signout-others"
            >
              {revoking === "all" ? "Signing out…" : "Sign out all other devices"}
            </Button>
          )}
        />
        <div className="rounded-xl border border-border overflow-hidden">
          {sessions.length === 0 ? (
            <div className="p-4 text-[13px] text-foreground/55">No active sessions tracked yet.</div>
          ) : sessions.map(s => (
            <div key={s.id} className="p-3.5 flex items-center gap-3 text-[13.5px] border-b border-border last:border-b-0" data-testid={`session-${s.id}`}>
              <ShieldCheck className="w-4 h-4 text-emerald-600"/>
              <div className="flex-1">
                <div className="font-medium">
                  {s.device}
                  {s.current && <span className="chip ml-2 bg-accent/20 text-foreground">Current</span>}
                </div>
                <div className="text-[11.5px] text-foreground/55">
                  {s.ip && s.ip !== "—" ? `${s.ip} · ` : ""}
                  Signed in {new Date(s.created_at).toLocaleString()}
                  {s.last_active_at && s.last_active_at !== s.created_at && (
                    <> · last active {new Date(s.last_active_at).toLocaleString()}</>
                  )}
                </div>
              </div>
              {!s.current && (
                <Button
                  size="sm" variant="outline" className="rounded-full text-[11px] h-7"
                  onClick={() => revokeOne(s.id)}
                  disabled={revoking === s.id}
                  data-testid={`session-revoke-${s.id}`}
                >
                  {revoking === s.id ? "…" : "Sign out"}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <div>
        <SectionHeader title="Activity log" desc="Recent actions on your account — useful for spotting anything you didn't do."/>
        {audit.length === 0 ? (
          <div className="text-[13px] text-foreground/55 py-6">No activity logged yet.</div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <ul className="divide-y divide-border">
              {audit.slice(0, 10).map(a => (
                <li key={a.id} className="p-3.5 flex items-center gap-3 text-[13px]" data-testid={`audit-${a.id}`}>
                  <Clock className="w-3.5 h-3.5 text-foreground/50"/>
                  <span className="flex-1">{a.message || a.kind}</span>
                  <span className="text-[11.5px] text-foreground/50">{new Date(a.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Privacy & Data — GDPR Art 15 + 17 */}
      <div className="border-t border-border pt-6">
        <SectionHeader
          title="Privacy & Data"
          desc="Your data is yours. Download a complete copy or remove your account."
        />
        <div className="flex flex-col sm:flex-row gap-2.5">
          <Button
            variant="outline"
            className="rounded-full"
            data-testid="dsar-export"
            onClick={async () => {
              try {
                const r = await api.get("/me/data-export", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `verqo-data-export-${Date.now()}.json`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Your data export is downloading");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Export failed");
              }
            }}
          >
            Download my data
          </Button>
          <Button
            variant="outline"
            className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10"
            data-testid="dsar-delete"
            onClick={async () => {
              if (!window.confirm("Permanently anonymise your account? You will be logged out immediately. Transactional records (orders, invoices) are retained for 7 years per AML/tax law and removed automatically thereafter.")) return;
              try {
                await api.post("/me/account/delete");
                toast.success("Account anonymised. Logging you out.");
                setTimeout(() => { window.location.href = "/login"; }, 1200);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Delete failed");
              }
            }}
          >
            Delete my account
          </Button>
        </div>
        <p className="text-[11.5px] text-foreground/55 mt-2.5">
          GDPR Art 15 / CCPA §1798.110 export · GDPR Art 17 / CCPA §1798.105 erasure.
          See our <a href="/legal/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </p>
      </div>

      {/* Danger zone (admin-managed) */}
      <div className="border-t border-border pt-6">
        <SectionHeader title="Account closure (managed)" desc="If you'd like a verqo admin to walk you through closure, contact support."/>
        <Button variant="outline" disabled className="rounded-full text-destructive border-destructive/40 opacity-60" data-testid="sec-delete">Contact support</Button>
      </div>
    </div>
  );
}

// ── Default terms (seller-only) ─────────────────────────────────────────
export function DefaultTermsPanel() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ default_terms: "NET_30", default_additional_credit: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then(r => {
      setTenant(r.data);
      setForm({
        default_terms: r.data?.settings?.default_terms || "NET_30",
        default_additional_credit: r.data?.settings?.default_additional_credit || 0,
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/tenant/me", {
        settings: {
          default_terms: form.default_terms,
          default_additional_credit: Number(form.default_additional_credit || 0),
        },
      });
      toast.success("Default terms saved");
    } finally { setSaving(false); }
  };

  if (!tenant) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  return (
    <div>
      <SectionHeader title="Default terms for new buyers" desc="Financial defaults applied when you send a new buyer invite — they can be overridden per-buyer in the invite flow. Lead time + shipping policies live in Public profile; warehouse mechanics (QI window, pickup window) live in Operations."/>
      <div>
        <FieldRow label="Default payment terms">
          <select value={form.default_terms} onChange={e => setForm({...form, default_terms: e.target.value})} className="w-full h-10 border border-foreground/15 rounded-md px-2 bg-white text-[14px]" data-testid="terms-default">
            <option value="PREPAY">Prepay</option>
            <option value="NET_14">Net-14</option>
            <option value="NET_30">Net-30</option>
            <option value="NET_60">Net-60</option>
            <option value="NET_90">Net-90</option>
          </select>
        </FieldRow>
        <FieldRow label="Default extra credit ($)" hint="On top of the verqo baseline credit line the buyer already has.">
          <Input type="number" min={0} step={1000} value={form.default_additional_credit} onChange={e => setForm({...form, default_additional_credit: e.target.value})} data-testid="terms-credit"/>
        </FieldRow>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={saving} data-testid="terms-save" className="rounded-full bg-foreground text-background">{saving ? "Saving…" : "Save defaults"}</Button>
      </div>
    </div>
  );
}

// ── Integrations & API (placeholder with links) ─────────────────────────
export function IntegrationsPanel() {
  // Internal infra (Plaid, Modern Treasury, Verqo Shipping, Middesk, Claude) is
  // platform-managed and intentionally hidden from end-user settings — buyers
  // and sellers shouldn't need to reason about underlying vendors. Only
  // user-facing connector previews remain.
  const integrations = [
    { id: "quickbooks", name: "QuickBooks",     desc: "Accounting sync", status: "coming" },
    { id: "shopify",    name: "Shopify Connector", desc: "Retail sync",  status: "coming" },
  ];
  return (
    <div>
      <SectionHeader title="Integrations & API" desc="Connect your existing tools to your portal. More integrations land every quarter."/>
      <div className="grid md:grid-cols-2 gap-3 mt-2">
        {integrations.map(i => (
          <div key={i.id} className="rounded-xl border border-border p-4 flex items-center gap-3" data-testid={`integ-${i.id}`}>
            <span className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><Globe className="w-4 h-4"/></span>
            <div className="flex-1">
              <div className="text-[14px] font-medium">{i.name}</div>
              <div className="text-[11.5px] text-foreground/55">{i.desc}</div>
            </div>
            {i.status === "live" ? <span className="chip bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">Live</span> : <ComingSoon/>}
          </div>
        ))}
      </div>
      <div className="mt-6">
        <SectionHeader title="Developer API keys" desc="Programmatic access to your portal." action={<ComingSoon>Coming soon</ComingSoon>}/>
        <Input disabled placeholder="No API keys generated yet — feature coming soon" className="opacity-60"/>
      </div>
    </div>
  );
}


// ── Two-factor authentication (TOTP + optional SMS via Twilio) ──────────
export function TwoFactorPanel() {
  const [status, setStatus] = useState(null);
  // TOTP setup state
  const [setup, setSetup] = useState(null); // { secret, qr_png_b64, uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Recovery codes — shown ONCE after enrollment or regenerate
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  // SMS state
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsStage, setSmsStage] = useState("idle"); // idle | sent

  const refresh = () => api.get("/auth/2fa/status").then(r => setStatus(r.data));
  useEffect(() => { refresh(); }, []);

  const startTotp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/totp/setup");
      setSetup(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const verifyTotp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/totp/verify", { code });
      toast.success("Authenticator enabled");
      setSetup(null); setCode("");
      // Backend returns `recovery_codes` ONLY on first-time enroll.
      // Capture + show them — they cannot be retrieved later.
      if (Array.isArray(data?.recovery_codes) && data.recovery_codes.length) {
        setRecoveryCodes(data.recovery_codes);
      }
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  const disableTotp = async () => {
    const c = window.prompt("Enter your current 6-digit code to confirm disabling TOTP:");
    if (!c) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/totp/disable", { code: c });
      toast.success("Authenticator disabled");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const regenerateRecovery = async () => {
    const c = window.prompt(
      "Enter your current 6-digit code to regenerate backup codes. " +
      "Your existing codes will all be invalidated.",
    );
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/totp/recovery-codes/regenerate", { code: c });
      setRecoveryCodes(data.recovery_codes || []);
      toast.success("Backup codes regenerated");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate codes");
    } finally { setBusy(false); }
  };

  const startSms = async () => {
    if (!phone.startsWith("+")) return toast.error("Use E.164 format, e.g. +14155551234");
    setBusy(true);
    try {
      await api.post("/auth/2fa/sms/start", { phone });
      setSmsStage("sent");
      toast.success("Code sent");
    } catch (e) { toast.error(e.response?.data?.detail || "SMS failed"); }
    finally { setBusy(false); }
  };

  const verifySms = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/sms/verify", { phone, code: smsCode });
      toast.success("SMS 2FA enabled");
      setSmsStage("idle"); setPhone(""); setSmsCode("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  const disableSms = async () => {
    // SMS-2FA disable now requires step-up (Jun 2026). Prompt for
    // either a TOTP code (if also enrolled) or a fresh SMS code.
    // First POST without a code → server returns the available factors
    // so we know exactly what to ask for.
    setBusy(true);
    let availableFactors = [];
    let phoneMasked = null;
    try {
      await api.post("/auth/2fa/sms/disable", {});
      // Backend will throw 401 stepup_required — flow lands in catch.
      toast.success("SMS 2FA disabled");
      refresh();
      return;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "stepup_required") {
        availableFactors = detail.available_factors || [];
        phoneMasked      = detail.phone_masked || null;
      } else {
        toast.error(detail?.message || detail || "Failed");
        return;
      }
    } finally { setBusy(false); }

    // Decide which factor to prompt the user for.
    const preferTotp = availableFactors.includes("totp");
    let code = null;
    if (preferTotp) {
      code = window.prompt(
        "Confirm with your authenticator app to disable SMS 2FA:\n\n" +
        "Enter your 6-digit code (or a backup recovery code):",
      );
      if (!code) return;
    } else if (availableFactors.includes("sms")) {
      try {
        await api.post("/sms/stepup/send", { action: "disable_sms" });
        toast.info(`Code sent to ${phoneMasked || "your phone"}`);
      } catch (e) {
        toast.error(e.response?.data?.detail?.message || "Could not send SMS code");
        return;
      }
      code = window.prompt(
        `Enter the 6-digit code we sent to ${phoneMasked || "your phone"}:`,
      );
      if (!code) return;
    } else {
      toast.error("No 2FA factor available to confirm with");
      return;
    }

    setBusy(true);
    try {
      const payload = preferTotp ? { totp_code: code.trim() } : { sms_code: code.trim() };
      await api.post("/auth/2fa/sms/disable", payload);
      toast.success("SMS 2FA disabled");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div data-testid="2fa-panel">
      <SectionHeader title="Two-factor authentication" desc="Require a code from your authenticator app or SMS on login. We strongly recommend TOTP — it works offline and is phishing-resistant."/>

      {/* TOTP card */}
      <div className="rounded-xl border border-border p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium">Authenticator app (TOTP)</div>
            <div className="text-[12px] text-foreground/60">Google Authenticator · 1Password · Authy · Microsoft Authenticator</div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${status.totp_enabled ? "bg-emerald-100 text-emerald-800" : "bg-foreground/10 text-foreground/60"}`} data-testid="totp-status">
            {status.totp_enabled ? "On" : "Off"}
          </span>
        </div>
        {!status.totp_enabled && !setup && (
          <Button onClick={startTotp} disabled={busy} data-testid="totp-start" className="rounded-full bg-foreground text-background">
            Enable authenticator
          </Button>
        )}
        {setup && (
          <div className="mt-2 space-y-3">
            <div className="text-[12px] text-foreground/65">Scan this QR with your authenticator app, then enter the 6-digit code below.</div>
            <img src={`data:image/png;base64,${setup.qr_png_b64}`} alt="TOTP QR" className="w-44 h-44 border border-border rounded"/>
            <details className="text-[11px] text-foreground/55">
              <summary className="cursor-pointer">Can't scan? Show secret manually</summary>
              <code className="mt-1 block break-all">{setup.secret}</code>
            </details>
            <div className="flex items-center gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="w-40" data-testid="totp-code"/>
              <Button onClick={verifyTotp} disabled={busy || code.length < 6} className="rounded-full bg-foreground text-background" data-testid="totp-verify">Verify</Button>
              <Button variant="outline" onClick={() => setSetup(null)} className="rounded-full">Cancel</Button>
            </div>
          </div>
        )}
        {status.totp_enabled && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={disableTotp} disabled={busy} className="rounded-full text-destructive border-destructive/30" data-testid="totp-disable">
              Disable authenticator
            </Button>
            <Button variant="outline" onClick={regenerateRecovery} disabled={busy} className="rounded-full" data-testid="recovery-regenerate">
              Regenerate backup codes
            </Button>
            <div className="text-[11.5px] text-foreground/55 ml-auto">
              {status.recovery_codes_remaining > 0
                ? `${status.recovery_codes_remaining} backup code${status.recovery_codes_remaining === 1 ? "" : "s"} left`
                : "No backup codes — regenerate now"}
            </div>
          </div>
        )}
      </div>

      {/* Recovery codes dialog — shown ONCE after enroll / regenerate */}
      {recoveryCodes && (
        <Dialog open={true} onOpenChange={(v) => !v && setRecoveryCodes(null)}>
          <DialogContent className="max-w-md" data-testid="recovery-codes-dialog">
            <DialogHeader>
              <DialogTitle>Save your backup codes</DialogTitle>
            </DialogHeader>
            <p className="text-[13px] text-foreground/70 leading-relaxed">
              <strong>This is the only time we'll show these codes.</strong> Keep them
              somewhere safe (a password manager works great). Each code works one
              time — use one if you lose access to your authenticator app.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-[14px] bg-secondary/50 border border-border rounded-xl p-4">
              {recoveryCodes.map(c => (
                <div key={c} className="text-center tracking-wider" data-testid={`recovery-code-${c}`}>{c}</div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline" className="rounded-full"
                onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Copied"); }}
                data-testid="recovery-copy"
              >Copy</Button>
              <Button
                variant="outline" className="rounded-full"
                onClick={() => {
                  const blob = new Blob([
                    "verqo · backup recovery codes\n",
                    "Generated " + new Date().toLocaleString() + "\n",
                    "Each code is single-use. Use any one in place of your authenticator code.\n\n",
                    recoveryCodes.join("\n"),
                  ], { type: "text/plain" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `verqo-backup-codes-${new Date().toISOString().slice(0,10)}.txt`;
                  a.click(); URL.revokeObjectURL(a.href);
                }}
                data-testid="recovery-download"
              >Download .txt</Button>
              <Button
                className="rounded-full bg-foreground text-background hover:bg-foreground/90 ml-auto"
                onClick={() => setRecoveryCodes(null)}
                data-testid="recovery-confirm"
              >I've saved them</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* SMS card */}
      <div className={`rounded-xl border border-border p-4 ${!status.sms_available ? "opacity-70" : ""}`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium">SMS code</div>
            <div className="text-[12px] text-foreground/60">
              {status.sms_available
                ? "Less secure than authenticator app, but easier to set up. Twilio Verify."
                : "Not configured on this deployment. Ask your admin to set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_VERIFY_SERVICE."}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${status.sms_enabled ? "bg-emerald-100 text-emerald-800" : "bg-foreground/10 text-foreground/60"}`} data-testid="sms-status">
            {status.sms_enabled ? "On" : "Off"}
          </span>
        </div>
        {status.sms_enabled ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground/70">{status.sms_phone}</span>
            <Button variant="outline" onClick={disableSms} disabled={busy} className="rounded-full text-destructive border-destructive/30" data-testid="sms-disable">
              Remove SMS
            </Button>
          </div>
        ) : status.sms_available ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155551234" className="w-52" data-testid="sms-phone" disabled={smsStage === "sent"}/>
              {smsStage === "idle" ? (
                <Button onClick={startSms} disabled={busy || !phone} className="rounded-full bg-foreground text-background" data-testid="sms-send">Send code</Button>
              ) : (
                <>
                  <Input value={smsCode} onChange={(e) => setSmsCode(e.target.value)} placeholder="Code" className="w-32" data-testid="sms-code"/>
                  <Button onClick={verifySms} disabled={busy || !smsCode} className="rounded-full bg-foreground text-background" data-testid="sms-verify">Verify</Button>
                  <Button variant="outline" onClick={() => setSmsStage("idle")} className="rounded-full">Cancel</Button>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
